/**
 * SourceUploader - Dark theme upload modal
 *
 * Supports three modes:
 * - FILE: Upload PDF or text files
 * - TEXT: Paste raw text content
 * - URL: Import from web URL
 */

import { useState, useRef } from 'react';

// Icons
const FileIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
);

const TextIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="17" y1="10" x2="3" y2="10" />
    <line x1="21" y1="6" x2="3" y2="6" />
    <line x1="21" y1="14" x2="3" y2="14" />
    <line x1="17" y1="18" x2="3" y2="18" />
  </svg>
);

const LinkIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);

const XIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const UploadIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

type UploadMode = 'file' | 'text' | 'url';

interface SourceUploaderProps {
  spaceId: string;
  onSuccess: () => void;
  onCancel: () => void;
}

export function SourceUploader({ spaceId, onSuccess, onCancel }: SourceUploaderProps) {
  const [mode, setMode] = useState<UploadMode>('file');
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [url, setUrl] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Handle file upload
  const handleFileChange = async (file: File) => {
    setIsUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', title || file.name);

      const response = await fetch(`/api/kb/spaces/${spaceId}/documents`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.error || 'Upload failed');
      }

      setTitle('');
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setIsUploading(false);
    }
  };

  // Handle text submission
  const handleTextSubmit = async () => {
    if (!text.trim()) {
      setError('Please enter some text');
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      const response = await fetch(`/api/kb/spaces/${spaceId}/documents`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: title || 'Text Note',
          sourceType: 'text',
          text,
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.error || 'Upload failed');
      }

      setTitle('');
      setText('');
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setIsUploading(false);
    }
  };

  // Handle URL submission
  const handleUrlSubmit = async () => {
    if (!url.trim()) {
      setError('Please enter a URL');
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      const response = await fetch(`/api/kb/spaces/${spaceId}/documents`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: title || url,
          sourceType: 'url',
          sourceUrl: url,
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.error || 'Failed to import URL');
      }

      setTitle('');
      setUrl('');
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to import URL');
    } finally {
      setIsUploading(false);
    }
  };

  // Drag and drop handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileChange(file);
  };

  return (
    <div className="source-uploader">
      {/* Header */}
      <div className="uploader-header">
        <h3 className="uploader-title">Add Source</h3>
        <button type="button" className="close-btn" onClick={onCancel}>
          <XIcon />
        </button>
      </div>

      {/* Mode tabs */}
      <div className="mode-tabs">
        <button
          type="button"
          className={`mode-tab ${mode === 'file' ? 'active' : ''}`}
          onClick={() => setMode('file')}
        >
          <FileIcon />
          <span>File</span>
        </button>
        <button
          type="button"
          className={`mode-tab ${mode === 'text' ? 'active' : ''}`}
          onClick={() => setMode('text')}
        >
          <TextIcon />
          <span>Text</span>
        </button>
        <button
          type="button"
          className={`mode-tab ${mode === 'url' ? 'active' : ''}`}
          onClick={() => setMode('url')}
        >
          <LinkIcon />
          <span>URL</span>
        </button>
      </div>

      {/* Title input (optional) */}
      <input
        type="text"
        className="title-input"
        placeholder="Title (optional)"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
      />

      {/* Mode-specific content */}
      {mode === 'file' && (
        <div
          className={`file-dropzone ${isDragging ? 'dragging' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.txt,.md,.doc,.docx"
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFileChange(file);
            }}
          />
          <div className="dropzone-icon">
            <UploadIcon />
          </div>
          <p className="dropzone-text">
            {isUploading ? 'Uploading...' : 'Drop file here or click to browse'}
          </p>
          <p className="dropzone-hint">PDF, TXT, MD, DOC supported</p>
        </div>
      )}

      {mode === 'text' && (
        <div className="text-upload">
          <textarea
            className="text-input"
            placeholder="Paste or write text content..."
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={6}
          />
          <button
            type="button"
            className="submit-btn"
            onClick={handleTextSubmit}
            disabled={isUploading || !text.trim()}
          >
            {isUploading ? 'Adding...' : 'Add Text'}
          </button>
        </div>
      )}

      {mode === 'url' && (
        <div className="url-upload">
          <input
            type="url"
            className="url-input"
            placeholder="https://example.com/article"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
          <button
            type="button"
            className="submit-btn"
            onClick={handleUrlSubmit}
            disabled={isUploading || !url.trim()}
          >
            {isUploading ? 'Importing...' : 'Import URL'}
          </button>
        </div>
      )}

      {/* Error message */}
      {error && <div className="error-message">{error}</div>}

      <style>{`
        .source-uploader {
          color: #e2e8f0;
        }

        .uploader-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 20px;
        }

        .uploader-title {
          font-size: 16px;
          font-weight: 600;
          color: #fff;
          margin: 0;
        }

        .close-btn {
          background: none;
          border: none;
          color: rgba(148, 163, 184, 0.6);
          cursor: pointer;
          padding: 4px;
          border-radius: 6px;
          transition: all 0.15s;
        }

        .close-btn:hover {
          color: #fff;
          background: rgba(255, 255, 255, 0.1);
        }

        .mode-tabs {
          display: flex;
          gap: 8px;
          margin-bottom: 16px;
        }

        .mode-tab {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 14px;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 8px;
          font-size: 13px;
          color: rgba(148, 163, 184, 0.8);
          cursor: pointer;
          transition: all 0.15s;
        }

        .mode-tab:hover {
          background: rgba(255, 255, 255, 0.08);
          color: #fff;
        }

        .mode-tab.active {
          background: rgba(99, 102, 241, 0.2);
          border-color: rgba(99, 102, 241, 0.4);
          color: #a5b4fc;
        }

        .title-input {
          width: 100%;
          padding: 12px 14px;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 10px;
          font-size: 14px;
          color: #fff;
          margin-bottom: 16px;
          outline: none;
          transition: border-color 0.15s;
        }

        .title-input::placeholder {
          color: rgba(100, 116, 139, 0.6);
        }

        .title-input:focus {
          border-color: rgba(99, 102, 241, 0.5);
        }

        /* File dropzone */
        .file-dropzone {
          padding: 32px 24px;
          border: 2px dashed rgba(255, 255, 255, 0.1);
          border-radius: 12px;
          text-align: center;
          cursor: pointer;
          transition: all 0.15s;
        }

        .file-dropzone:hover,
        .file-dropzone.dragging {
          border-color: rgba(99, 102, 241, 0.5);
          background: rgba(99, 102, 241, 0.05);
        }

        .dropzone-icon {
          color: rgba(99, 102, 241, 0.6);
          margin-bottom: 12px;
        }

        .dropzone-text {
          font-size: 14px;
          color: rgba(226, 232, 240, 0.8);
          margin: 0 0 4px 0;
        }

        .dropzone-hint {
          font-size: 12px;
          color: rgba(100, 116, 139, 0.6);
          margin: 0;
        }

        /* Text input */
        .text-input {
          width: 100%;
          padding: 12px 14px;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 10px;
          font-size: 14px;
          color: #fff;
          resize: vertical;
          outline: none;
          margin-bottom: 12px;
          font-family: inherit;
          transition: border-color 0.15s;
        }

        .text-input::placeholder {
          color: rgba(100, 116, 139, 0.6);
        }

        .text-input:focus {
          border-color: rgba(99, 102, 241, 0.5);
        }

        /* URL input */
        .url-input {
          width: 100%;
          padding: 12px 14px;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 10px;
          font-size: 14px;
          color: #fff;
          margin-bottom: 12px;
          outline: none;
          transition: border-color 0.15s;
        }

        .url-input::placeholder {
          color: rgba(100, 116, 139, 0.6);
        }

        .url-input:focus {
          border-color: rgba(99, 102, 241, 0.5);
        }

        /* Submit button */
        .submit-btn {
          width: 100%;
          padding: 12px 16px;
          background: #6366f1;
          color: #fff;
          border: none;
          border-radius: 10px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.15s;
        }

        .submit-btn:hover:not(:disabled) {
          background: #818cf8;
        }

        .submit-btn:disabled {
          background: rgba(99, 102, 241, 0.3);
          cursor: not-allowed;
        }

        /* Error message */
        .error-message {
          padding: 10px 14px;
          background: rgba(239, 68, 68, 0.1);
          border: 1px solid rgba(239, 68, 68, 0.3);
          border-radius: 8px;
          color: #fca5a5;
          font-size: 13px;
          margin-top: 12px;
        }
      `}</style>
    </div>
  );
}
